'use client';

import { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from "@/hooks/useTranslation";
import { Treatment } from '@/lib/treatments-data';
import { Button } from '@/components/ui/button';
import OptimizedImage from '@/components/optimized-media/OptimizedImage';
import { ArrowRight, ArrowLeft, Star, Shield, Award, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

gsap.registerPlugin(ScrollTrigger);

interface HeroSectionProps {
    treatment: Treatment;
}

const getBeforeAfterImages = (t: any) => [
    {
        before: '/images/treatments/all-on-4/cover-before.webp',
        after: '/images/treatments/all-on-4/cover-after.webp',
        description: t('hero.beforeAfter.descriptions.transformation')
    },
    {
        before: '/images/treatments/all-on-4/gallery-1-before.webp',
        after: '/images/treatments/all-on-4/gallery-1-after.webp',
        description: t('hero.beforeAfter.descriptions.rehabilitation')
    }
];

export default function AllOnFourHeroSection({ treatment }: HeroSectionProps) {
    const { t, isRTL } = useTranslation("all-on-4");
    const beforeAfterImages = getBeforeAfterImages(t);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [showAfter, setShowAfter] = useState(false);
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const subtitleRef = useRef<HTMLParagraphElement>(null);
    const ctaRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.set([titleRef.current, subtitleRef.current, ctaRef.current, imageRef.current], {
                opacity: 0,
                y: 60,
                scale: 0.95
            });

            gsap.to(titleRef.current, {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 1.2,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: titleRef.current,
                    start: "top 80%",
                    end: "bottom 20%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

            gsap.to(subtitleRef.current, {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 1,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: subtitleRef.current,
                    start: "top 75%",
                    end: "bottom 25%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

            gsap.to(ctaRef.current, {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 0.8,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: ctaRef.current,
                    start: "top 70%",
                    end: "bottom 30%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

            gsap.to(imageRef.current, {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 1.5,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: imageRef.current,
                    start: "top 75%",
                    end: "bottom 25%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentImageIndex((prev) => (prev + 1) % beforeAfterImages.length);
        }, 5000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const toggleInterval = setInterval(() => {
            setShowAfter(prev => !prev);
        }, 3000);

        return () => clearInterval(toggleInterval);
    }, []);

    const nextImage = () => {
        setCurrentImageIndex((prev) => (prev + 1) % beforeAfterImages.length);
    };

    const prevImage = () => {
        setCurrentImageIndex((prev) => (prev - 1 + beforeAfterImages.length) % beforeAfterImages.length);
    };

    const handleConsultationClick = () => {
        const gallerySection = document.getElementById('gallery');
        gallerySection?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleExploreClick = () => {
        const typesSection = document.getElementById('types');
        typesSection?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <section
            id="hero"
            ref={sectionRef}
            className="relative min-h-[65vh] bg-gradient-to-br from-navy to-navy-950 overflow-hidden"
        >
            <div className="absolute inset-0">
                <div className="absolute top-1/4 right-0 w-96 h-96 bg-gold opacity-10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-gold opacity-10 rounded-full blur-2xl"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gold opacity-5 rounded-full blur-3xl"></div>
            </div>

            <div className="container relative py-10 lg:py-16">
                <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[52vh]">
                    <div className="space-y-8 text-center lg:text-left">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className={`inline-flex items-center bg-gold/20 text-gold px-4 py-2 rounded-full text-sm font-medium ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
                        >
                            <Sparkles className="w-4 h-4" />
                            <span>{t("hero.badge")}</span>
                        </motion.div>

                        <h1
                            ref={titleRef}
                            className={`text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight font-serif ${isRTL ? 'text-right' : ''}`}
                        >
                            {t("hero.title")}{' '}
                            <span className="text-gold relative">
                                {t("hero.location")}
                                <motion.div
                                    className="absolute -inset-2 bg-gold/20 rounded-lg"
                                    animate={{
                                        boxShadow: [
                                            '0 0 20px rgba(212, 175, 55, 0.3)',
                                            '0 0 30px rgba(212, 175, 55, 0.5)',
                                            '0 0 20px rgba(212, 175, 55, 0.3)'
                                        ]
                                    }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                    }}
                                />
                            </span>
                        </h1>

                        <p
                            ref={subtitleRef}
                            className={`text-lg md:text-xl text-white/90 leading-relaxed max-w-xl ${isRTL ? 'text-right' : ''}`}
                        >
                            {t("hero.subtitle")}
                        </p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6, duration: 0.8 }}
                            className="flex flex-wrap justify-center lg:justify-start items-center gap-6 text-white/80"
                        >
                            <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                                <Star className="w-5 h-5 text-gold" />
                                <span className="text-sm font-medium">{t("hero.stats.successRate")}</span>
                            </div>
                            <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                                <Shield className="w-5 h-5 text-gold" />
                                <span className="text-sm font-medium">{t("hero.stats.sameDay")}</span>
                            </div>
                            <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                                <Award className="w-5 h-5 text-gold" />
                                <span className="text-sm font-medium">{t("hero.stats.expertCare")}</span>
                            </div>
                        </motion.div>

                        <div ref={ctaRef} className="flex flex-col sm:flex-row gap-4 pt-4">
                            <Button
                                onClick={handleConsultationClick}
                                className="bg-gold hover:bg-gold-600 text-navy font-semibold px-8 py-3 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl group"
                            >
                                {t("hero.buttons.getQuote")}
                                {isRTL ? (
                                    <ArrowLeft className="mr-2 w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                                ) : (
                                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                )}
                            </Button>
                            <Button
                                onClick={handleExploreClick}
                                variant="outline"
                                className="border-white/30 text-white hover:bg-white/10 hover:border-white/50 px-8 py-3 rounded-lg transition-all duration-300"
                            >
                                {t("hero.buttons.bookConsultation")}
                            </Button>
                        </div>
                    </div>

                    <div ref={imageRef} className="relative">
                        <div className="relative bg-white dark:bg-slate-800 rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-lg min-h-[400px]">
                            <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-slate-200 dark:from-slate-800 dark:to-navy-950"></div>

                            <div className="relative">
                                <div className="flex justify-center p-6 border-b border-slate-200 dark:border-slate-700">
                                    <div className="flex bg-slate-50 dark:bg-navy-950 rounded-lg p-1">
                                        <button
                                            onClick={() => setShowAfter(false)}
                                            className={`px-6 py-2 rounded-md font-medium transition-all ${!showAfter
                                                ? 'bg-gold text-navy shadow-sm'
                                                : 'text-slate-600 dark:text-slate-300 hover:text-gold'
                                                }`}
                                        >
                                            {t("hero.beforeAfter.before")}
                                        </button>
                                        <button
                                            onClick={() => setShowAfter(true)}
                                            className={`px-6 py-2 rounded-md font-medium transition-all ${showAfter
                                                ? 'bg-gold text-navy shadow-sm'
                                                : 'text-slate-600 dark:text-slate-300 hover:text-gold'
                                                }`}
                                        >
                                            {t("hero.beforeAfter.after")}
                                        </button>
                                    </div>
                                </div>

                                <div className="p-6 h-80">
                                    <div className="relative w-full h-full rounded-lg overflow-hidden">
                                        <AnimatePresence mode="wait">
                                            <motion.div
                                                key={`${currentImageIndex}-${showAfter}`}
                                                className="absolute inset-0"
                                                initial={{ opacity: 0, scale: 1.1 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 0.9 }}
                                                transition={{ duration: 0.6 }}
                                            >
                                                <OptimizedImage
                                                    src={showAfter ? beforeAfterImages[currentImageIndex].after : beforeAfterImages[currentImageIndex].before}
                                                    alt={`${showAfter ? t('hero.beforeAfter.after') : t('hero.beforeAfter.before')} - ${beforeAfterImages[currentImageIndex].description}`}
                                                    fill
                                                    sizes="(max-width: 1024px) 100vw, 50vw"
                                                />
                                            </motion.div>
                                        </AnimatePresence>

                                        <div className="absolute inset-y-0 left-0 flex items-center z-10">
                                            <Button
                                                onClick={prevImage}
                                                variant="outline"
                                                size="sm"
                                                className="ml-4 bg-white/90 hover:bg-white border-slate-200 shadow-md"
                                            >
                                                <ChevronLeft className="w-4 h-4" />
                                            </Button>
                                        </div>
                                        <div className="absolute inset-y-0 right-0 flex items-center z-10">
                                            <Button
                                                onClick={nextImage}
                                                variant="outline"
                                                size="sm"
                                                className="mr-4 bg-white/90 hover:bg-white border-slate-200 shadow-md"
                                            >
                                                <ChevronRight className="w-4 h-4" />
                                            </Button>
                                        </div>

                                        <div className="absolute bottom-4 left-4 right-4 z-10">
                                            <div className="bg-black/70 backdrop-blur-sm rounded-lg p-3">
                                                <p className="text-white text-sm font-medium">
                                                    {beforeAfterImages[currentImageIndex].description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className={`flex justify-center pb-6 ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                                    {beforeAfterImages.map((_, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setCurrentImageIndex(index)}
                                            className={`w-2 h-2 rounded-full transition-all ${index === currentImageIndex
                                                ? 'bg-gold w-6'
                                                : 'bg-slate-200 dark:bg-slate-700 hover:bg-gold/50'
                                                }`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
} 